import useSpecies from '@/hooks/species/useSpecies';
import RecorderIcon from '@/components/Icon/RecorderIcon';
import CameraIcon from '@/components/Icon/CameraIcon';
import { SpeciesAttributes, StrapiEntry } from '@/lib/apiTypes';
import {
	LargeSquareBackgroundLine,
	LargeSquareDividerLine,
} from '@/components/LargeSquareBackground/LargeSquareBackground';
import { motion, useReducedMotion } from 'framer-motion';
import cn from '@/lib/cs';
import { useMemo } from 'react';
import useHaptics from '@/hooks/useHaptics';

const SpotNowButton = ({
	species,
	openCamera,
	openRecorder,
}: {
	species: StrapiEntry<SpeciesAttributes>;
	openCamera: () => void;
	openRecorder: () => void;
}) => {
	const reduceMotion = useReducedMotion();

	const { withOnButtonPress } = useHaptics();

	// Check if audio recognition is supported for this species
	const { getSpeciesSupportsAudioRecognition } = useSpecies();
	const isRecorderEnabled = useMemo(
		() => getSpeciesSupportsAudioRecognition(species.id),
		[getSpeciesSupportsAudioRecognition, species.id]
	);

	return (
		<>
			<LargeSquareBackgroundLine className="scale-90 text-accentCompat" />
			{isRecorderEnabled && (
				<LargeSquareDividerLine className="text-accentCompat" />
			)}
			<div
				className={cn(
					'absolute inset-0 flex size-full gap-2',
					isRecorderEnabled ? 'px-8 rotate-12' : ''
				)}
			>
				<motion.button
					className={cn(
						'flex size-full items-center justify-center rounded-3xl pb-8',
						isRecorderEnabled ? 'pb-16 ps-2 pe-2' : ''
					)}
					onClick={openCamera}
					{...withOnButtonPress}
				>
					<motion.div
						animate={
							reduceMotion
								? undefined
								: {
										scale: [1, 1.15],
										transition: {
											repeat: isRecorderEnabled ? 3 : 4,
											repeatType: 'reverse',
											duration: 1.5,
											ease: 'easeInOut',
										},
										 
								  }
						}
					>
						<CameraIcon
							className={
								isRecorderEnabled
									? 'h-24 -rotate-12 lg:h-24'
									: 'h-32 lg:h-32'
							}
						/>
					</motion.div>
				</motion.button>
				{isRecorderEnabled && (
					<motion.button
						className={cn(
							'flex size-full items-center justify-center rounded-3xl pb-8',
							isRecorderEnabled ? 'pt-12' : ''
						)}
						onClick={openRecorder}
						{...withOnButtonPress}
					>
						<motion.div
							animate={
								reduceMotion
									? undefined
									: {
											scale: [1, 1.15],
											transition: {
												repeat: 3,
												repeatType: 'reverse',
												delay: 1,
												duration: 1.5,
												ease: 'easeInOut',
											},
											 
									  }
							}
						>
							<RecorderIcon className="h-16  -translate-y-4 -rotate-6 lg:h-16" />
						</motion.div>
					</motion.button>
				)}
			</div>
			<span className="absolute bottom-10 font-brand text-4xl">
				Spot nu!
			</span>
		</>
	);
};

export default SpotNowButton;
